<template>
  <div>
    <el-menu
      class="sidebar"
      :default-active="$route.path"
      :collapse="!sidebar.opened"
      background-color="#304156"
      text-color="#bfcbd9"
      active-text-color="#409EFF"
      :router="!isMenuNavActive"
      :hide-timeout="0"
      :show-timeout="0"
      :collapse-transition="false"
      :popper-offset="0"
    >
      <div class="mobile-close-bar">
        <el-icon @click="closeSidebar">
          <Close />
        </el-icon>
      </div>
      <!-- Sub-menu drill-down view -->
      <template v-if="isInSubMenuDrillDown">
        <div class="submenu-drilldown-header">
          <el-icon><ArrowLeft /></el-icon>
          <span>{{ expandedParentTitle }}</span>
        </div>
        <el-menu-item
          v-for="(child, index) in expandedChildren"
          :key="child.path"
          :index="expandedParentPath + '/' + child.path"
          class="menu-nav-item"
        >
          <span v-if="getLetterForIndex" class="menu-hotkey-badge">{{ getLetterForIndex(index) }}</span>
          <el-icon v-if="child.meta && child.meta.icon">
            <component :is="child.meta.icon" />
          </el-icon>
          <span v-if="child.meta && (child.meta.alternativeTitle || child.meta.title)">
            {{ generateTitle(sidebar.opened ? child.meta.alternativeTitle || child.meta.title : child.meta.title) }}
          </span>
        </el-menu-item>
      </template>

      <!-- Normal menu view -->
      <template v-else>
        <template v-for="(item, index) in routesToDisplay" :key="item.path">
          <el-menu-item v-if="!item.children || item.children.length < 2" :index="item.path" @click="handleRouteClick">
            <span v-if="isMenuNavActive && getLetterForIndex" class="menu-hotkey-badge">
              {{ getLetterForIndex(index) }}
            </span>
            <el-icon v-if="item.meta && item.meta.icon">
              <component :is="item.meta.icon" />
            </el-icon>
            <template v-if="item.meta && item.meta.title" #title>
              {{ generateTitle(item.meta.title) }}
            </template>
          </el-menu-item>

          <el-sub-menu v-else :key="item.name || item.path" :index="item.name || item.path">
            <template v-if="item.meta && item.meta.title" #title>
              <span v-if="isMenuNavActive && getLetterForIndex" class="menu-hotkey-badge">
                {{ getLetterForIndex(index) }}
              </span>
              <el-icon v-if="item.meta && item.meta.icon">
                <component :is="item.meta.icon" />
              </el-icon>
              <span :title="generateTitle(item.meta.title)">
                {{ generateTitle(item.meta.title) }}
              </span>
            </template>

            <template v-for="child in item.children" :key="child.name">
              <el-menu-item :index="item.path + '/' + child.path" @click="handleRouteClick">
                <el-icon v-if="child.meta && child.meta.icon">
                  <component :is="child.meta.icon" />
                </el-icon>
                <span v-if="child.meta && (child.meta.alternativeTitle || child.meta.title)">
                  {{
                    generateTitle(sidebar.opened ? child.meta.alternativeTitle || child.meta.title : child.meta.title)
                  }}
                </span>
              </el-menu-item>
            </template>
          </el-sub-menu>
        </template>
      </template>

      <!-- Hint bar when menu nav is active -->
      <div v-if="isMenuNavActive" class="menu-nav-hint">
        <span>{{ $t(isInSubMenuDrillDown ? 'hotkeys.menuNav.hintBack' : 'hotkeys.menuNav.hintRoot') }}</span>
        <span class="menu-nav-hint-separator">·</span>
        <span>{{ $t(isInSubMenuDrillDown ? 'hotkeys.menuNav.hintBack' : 'hotkeys.menuNav.hintEsc') }}</span>
      </div>

      <app-version></app-version>
    </el-menu>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { ArrowLeft, Close } from '@element-plus/icons-vue'

import { routes } from '@/router'
import { basicRouteGuard } from '@/router/guards'
import { generateTitle } from '@/utils/i18n'

import AppVersion from './AppVersion.vue'

export default {
  components: { AppVersion, ArrowLeft, Close },
  props: {
    isMenuNavActive: {
      type: Boolean,
      default: false
    },
    expandedSubMenuIndex: {
      type: Number,
      default: null
    },
    getLetterForIndex: {
      type: Function,
      default: null
    }
  },
  computed: {
    ...mapGetters(['roles', 'sidebar']),
    isCollapse() {
      return !this.sidebar.opened
    },
    routesToDisplay() {
      const { children: layoutRoutes } = routes.find(route => route.sidebarElements)

      return layoutRoutes.reduce((acc, layoutRoute) => {
        const isRouteAvailable = basicRouteGuard(layoutRoute)

        if (!isRouteAvailable) return acc

        if (!layoutRoute.children) {
          return [...acc, layoutRoute]
        }

        const filteredChildren = layoutRoute.children.filter(routeChild => basicRouteGuard(routeChild))

        if (filteredChildren.length === 1) {
          const [filteredChild] = filteredChildren
          const singleChildRoute = {
            ...filteredChild,
            path: `${layoutRoute.path}/${filteredChild.path}`
          }

          return [...acc, singleChildRoute]
        }

        return [...acc, { ...layoutRoute, children: filteredChildren }]
      }, [])
    },
    isInSubMenuDrillDown() {
      return this.isMenuNavActive && this.expandedSubMenuIndex !== null
    },
    expandedParent() {
      if (this.expandedSubMenuIndex === null) return null
      return this.routesToDisplay[this.expandedSubMenuIndex]
    },
    expandedParentTitle() {
      const generateTitle = this.generateTitle
      return this.expandedParent?.meta?.title ? generateTitle(this.expandedParent.meta.title) : ''
    },
    expandedParentPath() {
      return this.expandedParent?.path ?? ''
    },
    expandedChildren() {
      return this.expandedParent?.children ?? []
    }
  },
  methods: {
    closeSidebar() {
      this.$store.dispatch('closeSideBar')
    },
    handleRouteClick() {
      if (window.matchMedia('(max-width: 767px)').matches) {
        this.closeSidebar()
      }
    },
    generateTitle
  }
}
</script>
<style rel="stylesheet/scss" lang="scss" scoped>
@use '@/styles/mixin';

.mobile-close-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 56px;
  padding: 0 20px;
  font-size: 25px;
  color: rgb(191, 203, 217);
  transition: color 0.2s linear;

  &:hover {
    color: rgb(64, 158, 255);
  }

  @include mixin.for-tablet-up {
    display: none;
  }
}

.menu-hotkey-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  margin-right: 6px;
  padding: 0 4px;
  font-size: 11px;
  font-weight: 700;
  font-family: monospace;
  text-transform: uppercase;
  color: #fff;
  background-color: var(--el-color-primary);
  border-radius: 3px;
}

.submenu-drilldown-header {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 20px;
  font-size: 14px;
  color: rgb(191, 203, 217);
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: color 0.2s linear;

  &:hover {
    color: rgb(64, 158, 255);
  }
}

.menu-nav-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 12px;
  font-size: 11px;
  color: rgba(191, 203, 217, 0.6);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.menu-nav-hint-separator {
  margin: 0 2px;
}
</style>
